import React from 'react';
import { RefreshCw, WifiOff, AlertCircle } from 'lucide-react';

interface ErrorBannerProps {
  message: string;
  onRetry?: () => void;
  errorCode?: string;
}

const ErrorBanner: React.FC<ErrorBannerProps> = ({ message, onRetry, errorCode }) => {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 animate-fade-in">
      <div className="w-20 h-20 bg-error-container/20 text-error rounded-[2rem] flex items-center justify-center mb-8 shadow-sm">
        <WifiOff size={40} />
      </div>
      
      <div className="space-y-4 max-w-md mx-auto">
        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-error/60">
            Connection Failure
          </span>
          <h2 className="text-3xl font-display font-black text-navy tracking-tight">
            Unable to synchronize dashboard
          </h2>
        </div>
        
        <p className="text-secondary font-medium leading-relaxed">
          {message || "An unexpected error occurred while communicating with the intelligence orchestration layer. Our systems are currently recovering."}
        </p>

        <div className="pt-6 flex flex-col items-center gap-4">
          <button 
            onClick={onRetry}
            className="flex items-center gap-3 px-8 py-4 bg-navy text-white rounded-2xl hover:bg-navy/90 transition-all font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 group"
          >
            <RefreshCw size={18} className="group-active:animate-spin" />
            Retry Connection
          </button>
          
          <p className="text-[10px] font-bold text-secondary/40 uppercase tracking-widest">
            ErrorCode: {errorCode || 'BFF_UNKNOWN_ERROR'}
          </p>
        </div>
      </div>
    </div>
  );
};

export const RootErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface p-6">
      <div className="max-w-md w-full bg-surface-container-lowest rounded-[2rem] p-10 shadow-ambient text-center space-y-6">
        <div className="w-20 h-20 bg-error-container/20 text-error rounded-3xl flex items-center justify-center mx-auto">
          <AlertCircle size={40} />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-black text-navy">Application Error</h2>
          <p className="text-secondary text-sm leading-relaxed">
            Something went wrong while rendering the interface.
          </p>
          <div className="bg-error-container/10 p-3 rounded-xl text-[10px] font-mono text-error/70 break-all text-left">
            {error.message}
          </div>
        </div>
        <button
          onClick={resetErrorBoundary}
          className="w-full py-4 bg-navy text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all"
        >
          Reload Interface
        </button>
      </div>
    </div>
  );
};

export default ErrorBanner;
